import { Disclosure } from '@headlessui/react';
import React from 'react';
import { EntryBox } from './EntryBox';
import { ItemTypes } from './ItemTypes';
import { ChevronUpIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import { SectionBox } from './SectionBox';

interface DraggableSectionsProps {
  isTimelineSupported?: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}

const DraggableSections: React.FC<DraggableSectionsProps> = ({isTimelineSupported, isCollapsed, setIsCollapsed}) => {
    return (
      <div className={`absolute -right-8 top-0 h-full transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-12' : 'w-72'
      }`}>
        <div className="h-full bg-white border-t border-l border-gray-200 shadow-lg rounded-tl-md rounded-bl-md">
          <div className="h-full flex flex-col">
            <div className="flex items-center gap-x-2 p-2 border-b border-gray-200">
              <button 
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                aria-label={isCollapsed ? "Expand sections" : "Collapse sections"}
              >
                <ChevronLeftIcon className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                  isCollapsed ? '' : 'rotate-180'
                }`} />
              </button>
              {!isCollapsed && ( 
                <div data-tour-id="template-editor-screen-drag-and-drop-sections">
                  <p className="font-medium text-gray-700 leading-5">Add to Template<br />
                    <span className="font-light text-sm text-gray-500">Drag and drop to reorder</span>
                  </p>
                </div>
              )}
            </div>
            <div className={`flex-1 overflow-y-auto thin-scrollbar transition-opacity duration-300 ${
              isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}>
              <div className="p-4 pb-8 flex flex-col gap-y-4">
                <SectionBox name="Section" description="Top level sections for notes" type={ItemTypes.SECTION}/>
                <EntryBox name="Entry" description='Data entry inside of a section' type={ItemTypes.ENTRY}/>
                <EntryBox name="Spaces" description='Add spaces between sections' type={ItemTypes.SPACES}/>
                {isTimelineSupported && <SectionBox name="Timeline" description="Summarize records by date" type={ItemTypes.TIMELINE}/>}
                <div data-tour-id="template-prebuilt-sections" className='flex flex-col gap-y-4'>
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full justify-between rounded-lg bg-blue-100 px-4 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                          <span>Pre-built sections</span>
                          <ChevronUpIcon
                            className={`${
                              open ? 'rotate-180 transform' : ''
                            } h-5 w-5 text-blue-500`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className="pb-2 text-sm text-gray-500 flex flex-col gap-y-4">
                          <SectionBox name="Subjective" description='Patient history' type={ItemTypes.SUBJECTIVE} />
                          <SectionBox name="Vitals" description='Temperature, weight, BP, etc.' type={ItemTypes.VITALS} />
                          <SectionBox name="Physical Exam" description='Body system assessments.' type={ItemTypes.PHYSICAL_EXAM} />
                          <SectionBox name="Assessment" description='Problems and DDX.' type={ItemTypes.ASSESSMENT} />
                          <SectionBox name="Plan" description='Tests, treatments, medication, etc.' type={ItemTypes.PLAN} />
                          <SectionBox name="Fear Free" description='Entries for Fear Free related information.' type={ItemTypes.FEAR_FREE} />
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full justify-between rounded-lg bg-blue-100 px-4 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                          <span>Pre-built entries</span>
                          <ChevronUpIcon
                            className={`${
                              open ? 'rotate-180 transform' : ''
                            } h-5 w-5 text-blue-500`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className="pb-2 text-sm text-gray-500 flex flex-col gap-y-4">
                          <EntryBox name="Owner Discussion" description='' type={ItemTypes.OWNER_DISCUSSION} />
                          <EntryBox name="Current Medication" description='' type={ItemTypes.CURRENT_MEDICATION} />
                          <EntryBox name="Current Diet" description='' type={ItemTypes.CURRENT_DIET} />
                          <EntryBox name="Current Preventatives" description='' type={ItemTypes.CURRENT_PREVENTATIVES} />
                          <EntryBox name="Medical History" description='' type={ItemTypes.CHRONIC_ISSUES} />
                          <EntryBox name="Diagnostic Tests" description='' type={ItemTypes.DIAGNOSTIC_TESTS} />
                          <EntryBox name="Treatments" description='' type={ItemTypes.TREATMENTs} />
                          <EntryBox name="Vaccines" description='' type={ItemTypes.VACCINES} />
                          <EntryBox name="Medication" description='' type={ItemTypes.MEDICATION} />
                          <EntryBox name="Diet" description='' type={ItemTypes.DIET} />
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full justify-between rounded-lg bg-blue-100 px-4 py-2 text-left text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                          <span>Charts</span>
                          <ChevronUpIcon
                            className={`${
                              open ? 'rotate-180 transform' : ''
                            } h-5 w-5 text-blue-500`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className="pb-2 text-sm text-gray-500 flex flex-col gap-y-4">
                          <SectionBox name="Canine Dental Chart" description='Dental chart for canines' type={ItemTypes.CANINE_DENTAL_CHART}/>
                          <SectionBox name="Feline Dental Chart" description='Dental chart for felines' type={ItemTypes.FELINE_DENTAL_CHART}/>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
};

export default DraggableSections;